/**
 * Skip Flow Params Utility
 * Centralized parsing, validation, and persistence of skip_intro and auto_return flags
 */

import { runtimeConfig } from '../services/RuntimeConfigService';
import { sessionStateManager } from './SessionStateManager';
import { getLaunchCredential } from './launchCredentials';
import { validateRedirectUrl } from './redirectHandler';
import { isNewTabMode } from './newTabHandler';

type StoredFlags = {
  skipIntro?: boolean;
  autoReturn?: boolean;
};

type StoredFlagRecord = {
  value: boolean;
  flowKey?: string;
  timestamp?: number;
};

export type AutoReturnStatus = 'failed' | 'completed';

type StoredStatusRecord = {
  status: AutoReturnStatus;
  flowKey?: string;
  timestamp?: number;
};

let inMemoryFlags: StoredFlags | null = null;
let inMemoryFlowKey: string | null = null;
let inMemoryAutoReturnStatus: StoredStatusRecord | null = null;

const STORAGE_KEYS = {
  skipIntro: 'sp_skip_intro',
  autoReturn: 'sp_auto_return',
  autoReturnStatus: 'sp_auto_return_status',
} as const;

function setInMemoryFlags(flags: StoredFlags, flowKey: string | null): void {
  if (inMemoryFlowKey && flowKey && inMemoryFlowKey !== flowKey) {
    inMemoryFlags = null;
  }
  if (!inMemoryFlags) {
    inMemoryFlags = {};
  }
  if (flags.skipIntro !== undefined) {
    inMemoryFlags.skipIntro = flags.skipIntro;
  }
  if (flags.autoReturn !== undefined) {
    inMemoryFlags.autoReturn = flags.autoReturn;
  }
  inMemoryFlowKey = flowKey;
  if (
    inMemoryFlags.skipIntro === undefined &&
    inMemoryFlags.autoReturn === undefined
  ) {
    inMemoryFlags = null;
    inMemoryFlowKey = null;
  }
}

function getInMemoryFlags(currentFlowKey: string | null): StoredFlags | null {
  if (!inMemoryFlags) return null;
  if (inMemoryFlowKey && currentFlowKey && inMemoryFlowKey === currentFlowKey) {
    return inMemoryFlags;
  }
  if (!inMemoryFlowKey && !currentFlowKey) {
    return inMemoryFlags;
  }
  inMemoryFlags = null;
  inMemoryFlowKey = null;
  return null;
}

function getCurrentFlowKey(): string | null {
  // Launch credentials keep the same flow key after they are removed from
  // the address bar.
  const params = new URLSearchParams(window.location.search);
  const sessionToken = getLaunchCredential('sessionToken');
  if (sessionToken) return `token:${sessionToken}`;

  const handoffToken = getLaunchCredential('handoffToken');
  const sessionId = params.get('sessionId');
  if (handoffToken && sessionId) return `handoff:${sessionId}:${handoffToken}`;
  if (sessionId) return `session:${sessionId}`;

  let state = sessionStateManager.getState();
  if (!state?.sessionId && !state?.config?.sessionToken) {
    try {
      const loaded = sessionStateManager.loadState();
      if (loaded) state = loaded;
    } catch {
      // Ignore storage access errors
    }
  }

  if (state?.config?.sessionToken) return `token:${state.config.sessionToken}`;
  if (state?.sessionId) return `session:${state.sessionId}`;

  if (runtimeConfig.isLoaded()) {
    const cfg = runtimeConfig.getConfig();
    if (cfg.sessionToken) return `token:${cfg.sessionToken}`;
    if (cfg.sessionId) return `session:${cfg.sessionId}`;
    if (cfg.handoffToken && cfg.sessionId) {
      return `handoff:${cfg.sessionId}:${cfg.handoffToken}`;
    }
  }

  return null;
}

function readStoredFlagRecord(key: string): StoredFlagRecord | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as StoredFlagRecord | boolean;
      if (typeof parsed === 'boolean') {
        return { value: parsed };
      }
      if (parsed && typeof parsed.value === 'boolean') {
        return parsed;
      }
    } catch {
      // Fall through to raw parsing.
    }
    if (raw === 'true' || raw === 'false') {
      return { value: raw === 'true' };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredFlagRecord(key: string, value: boolean, flowKey: string): void {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ value, flowKey, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
}

function readStoredStatusRecord(key: string): StoredStatusRecord | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredStatusRecord;
    if (parsed && typeof parsed.status === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredStatusRecord(status: AutoReturnStatus, flowKey: string): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEYS.autoReturnStatus,
      JSON.stringify({ status, flowKey, timestamp: Date.now() })
    );
  } catch {
    // Ignore storage errors
  }
}

function clearStoredFlagStorage(keys: Array<keyof StoredFlags>): void {
  try {
    if (keys.includes('skipIntro')) {
      sessionStorage.removeItem(STORAGE_KEYS.skipIntro);
    }
    if (keys.includes('autoReturn')) {
      sessionStorage.removeItem(STORAGE_KEYS.autoReturn);
    }
  } catch {
    // Ignore storage errors
  }
}

export function parseSkipFlowParams(): { skipIntro: boolean; autoReturn: boolean } {
  const params = new URLSearchParams(window.location.search);
  const hasSkipIntro = params.has('skip_intro');
  const hasAutoReturn = params.has('auto_return');
  const flowKey = getCurrentFlowKey();
  const parsed = {
    skipIntro: params.get('skip_intro') === 'true',
    autoReturn: params.get('auto_return') === 'true',
  };
  setInMemoryFlags({
    skipIntro: hasSkipIntro ? parsed.skipIntro : undefined,
    autoReturn: hasAutoReturn ? parsed.autoReturn : undefined,
  }, flowKey);
  return parsed;
}

export function storeSkipFlowParams(flags: StoredFlags): void {
  const flowKey = getCurrentFlowKey();
  setInMemoryFlags(flags, flowKey);
  if (!flowKey) {
    return;
  }
  if (flags.skipIntro !== undefined) {
    writeStoredFlagRecord(STORAGE_KEYS.skipIntro, !!flags.skipIntro, flowKey);
  }
  if (flags.autoReturn !== undefined) {
    writeStoredFlagRecord(STORAGE_KEYS.autoReturn, !!flags.autoReturn, flowKey);
  }
}

export function getStoredSkipFlowParams(
  options?: { clearAfterRead?: boolean }
): StoredFlags | null {
  const currentFlowKey = getCurrentFlowKey();
  const inMemory = getInMemoryFlags(currentFlowKey);
  const storedSkip = readStoredFlagRecord(STORAGE_KEYS.skipIntro);
  const storedAuto = readStoredFlagRecord(STORAGE_KEYS.autoReturn);

  const resolveStoredFlag = (record: StoredFlagRecord | null, key: string) => {
    if (!record) return undefined;
    if (!record.flowKey) {
      clearStoredFlagStorage([
        key === STORAGE_KEYS.skipIntro ? 'skipIntro' : 'autoReturn',
      ]);
      return undefined;
    }
    if (!currentFlowKey || record.flowKey !== currentFlowKey) {
      clearStoredFlagStorage([
        key === STORAGE_KEYS.skipIntro ? 'skipIntro' : 'autoReturn',
      ]);
      return undefined;
    }
    return record.value;
  };

  const storedFlags: StoredFlags = {
    skipIntro: resolveStoredFlag(storedSkip, STORAGE_KEYS.skipIntro),
    autoReturn: resolveStoredFlag(storedAuto, STORAGE_KEYS.autoReturn),
  };

  const mergedFlags = {
    ...(storedFlags || {}),
    ...(inMemory || {}),
  };

  const hasFlags =
    mergedFlags.skipIntro !== undefined || mergedFlags.autoReturn !== undefined;
  if (!hasFlags) {
    return null;
  }

  if (options?.clearAfterRead) {
    const keysToClear: Array<keyof StoredFlags> = [];
    if (mergedFlags.skipIntro !== undefined) keysToClear.push('skipIntro');
    if (mergedFlags.autoReturn !== undefined) keysToClear.push('autoReturn');
    clearSkipFlowParams(keysToClear);
  }

  return mergedFlags;
}

export function clearSkipFlowParams(keys?: Array<keyof StoredFlags>): void {
  const toClear = keys ?? ['skipIntro', 'autoReturn'];
  clearStoredFlagStorage(toClear);
  if (inMemoryFlags) {
    if (toClear.includes('skipIntro')) {
      delete inMemoryFlags.skipIntro;
    }
    if (toClear.includes('autoReturn')) {
      delete inMemoryFlags.autoReturn;
    }
    if (
      inMemoryFlags.skipIntro === undefined &&
      inMemoryFlags.autoReturn === undefined
    ) {
      inMemoryFlags = null;
      inMemoryFlowKey = null;
    }
  }
}

export function canSkipIntro(): { ok: boolean; reason?: string } {
  // Require a valid session token from either runtimeConfig or persisted session state
  try {
    let hasToken = false;
    if (runtimeConfig.isLoaded()) {
      const cfg = runtimeConfig.getConfig();
      hasToken = !!cfg.sessionToken;
    }
    if (!hasToken) {
      const persisted = sessionStateManager.getState();
      hasToken = !!persisted?.config?.sessionToken;
    }
    return hasToken
      ? { ok: true }
      : { ok: false, reason: 'missing_session_token' };
  } catch {
    return { ok: false, reason: 'config_unavailable' };
  }
}

export function canAutoReturn(): { ok: boolean; returnUrl?: string; reason?: string } {
  // Resolve returnUrl only from authenticated runtime or persisted session state.
  try {
    const state = sessionStateManager.getState();
    let returnUrl = state?.config?.returnUrl || undefined;
    if (!returnUrl && runtimeConfig.isLoaded()) {
      returnUrl = runtimeConfig.getConfig().returnUrl || undefined;
    }
    if (!returnUrl) {
      return { ok: false, reason: 'missing_return_url' };
    }
    if (!validateRedirectUrl(returnUrl)) {
      return { ok: false, returnUrl, reason: 'invalid_return_url' };
    }
    return { ok: true, returnUrl };
  } catch {
    return { ok: false, reason: 'validation_error' };
  }
}

export function getAutoReturnRequested(): boolean {
  try {
    const status = getAutoReturnStatus();
    if (status === 'failed') {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    const hasAutoReturn = params.has('auto_return');
    const parsed = parseSkipFlowParams();
    if (hasAutoReturn) {
      return parsed.autoReturn;
    }
    const stored = getStoredSkipFlowParams();
    return stored?.autoReturn ?? parsed.autoReturn ?? false;
  } catch {
    return false;
  }
}

export function shouldAutoReturnInRedirect(): boolean {
  return getAutoReturnRequested() && !isNewTabMode();
}

export function setAutoReturnStatus(status: AutoReturnStatus): void {
  const flowKey = getCurrentFlowKey();
  inMemoryAutoReturnStatus = { status, flowKey: flowKey || undefined };
  if (flowKey) {
    writeStoredStatusRecord(status, flowKey);
  }
}

export function getAutoReturnStatus(): AutoReturnStatus | null {
  const currentFlowKey = getCurrentFlowKey();

  if (inMemoryAutoReturnStatus) {
    const statusFlowKey = inMemoryAutoReturnStatus.flowKey || null;
    if (
      (!statusFlowKey && !currentFlowKey) ||
      (statusFlowKey && currentFlowKey && statusFlowKey === currentFlowKey)
    ) {
      return inMemoryAutoReturnStatus.status;
    }
    inMemoryAutoReturnStatus = null;
  }

  const stored = readStoredStatusRecord(STORAGE_KEYS.autoReturnStatus);
  if (!stored) return null;
  if (!stored.flowKey) {
    clearAutoReturnStatus();
    return null;
  }
  if (!currentFlowKey || stored.flowKey !== currentFlowKey) {
    clearAutoReturnStatus();
    return null;
  }
  return stored.status;
}

export function clearAutoReturnStatus(): void {
  inMemoryAutoReturnStatus = null;
  try {
    sessionStorage.removeItem(STORAGE_KEYS.autoReturnStatus);
  } catch {
    // Ignore storage errors
  }
}

export function stripSkipFlowParamsFromUrl(keys?: Array<'skip_intro' | 'auto_return'>): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const toRemove = keys ?? ['skip_intro', 'auto_return'];
    let changed = false;

    for (const key of toRemove) {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    }

    if (!changed) return;

    const url = new URL(window.location.href);
    const nextSearch = params.toString();
    url.search = nextSearch ? `?${nextSearch}` : '';
    window.history.replaceState({}, '', url.toString());
  } catch {
    // Ignore URL update failures
  }
}
